import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import validator from 'validator';
import {
  useCreateUserMutation,
  useUpdateUserDataMutation,
  useUpdateOrganizationAdminUserDataMutation
} from '../../../redux/api/users';
import { usersErrorMsg } from '../../../Utils/errorMessages';
import LoadingAnimation from '../../Utils/BateryChargeAnimation';
import Button from '../../Utils/Button';
import FormInput from '../../Utils/Form/FormInput';
import inputTypes from '../../Utils/Form/FormInput/inputTypes';
import IfRender from '../../Utils/IfRender';
import ModalFrame from '../ModalFrame';
import RoleSettings from '../RoleSettings';
import './style.scss';
import { ORGANIZATION_TYPE, RFID_TAG_STATUS, ROLES } from '../../../Constants.js';
import { useSelector } from 'react-redux';
import { selectUserType } from '../../../redux/selectors/userType.js';
import {
  useCreateUserRfidTagMutation,
  useUpdateUserRfidTagMutation,
  useCreateAdminRfidTagMutation,
  useUpdateAdminRfidTagMutation
} from '../../../redux/api/users-rfidTags.js';
import {
  useCreateUserOrganizationsRolesMutation,
  useUpdateUserOrganizationsRolesMutation,
  useDeleteUserOrganizationsRoleMutation,
  useCreateSuperAdminRoleMutation
} from '../../../redux/api/user-organization-roles.js';

import cx from 'classnames';
import { useRequestOrganizationsTypeQuery } from '../../../redux/api/organizations.js';

export default function UserModal({
  cacheParams = {},
  userData = {},
  roles = [],
  refetch,
  toggleModal = () => {},
  userAuthData = {},
  organizations = [],
  providers = {}
}) {
  // RTK Query hooks with error states
  const [createUser, { isSuccess: isCreateSuccess, isError: isCreateError, error: createError }] =
    useCreateUserMutation();

  const [
    updateUserData,
    {
      isSuccess: isUpdateSuccess,
      isError: isUpdateError,
      error: updateError,
      isLoading: isUpdateLoading
    }
  ] = useUpdateUserDataMutation();

  const [updateOrganizationAdmin] = useUpdateOrganizationAdminUserDataMutation();
  const { data: organizationTypes } = useRequestOrganizationsTypeQuery();
  const [createUserRfidTag] = useCreateUserRfidTagMutation();
  const [
    createAdminRfidTag,
    {
      isError: isCreateAdminRfidTagError,
      error: createAdminRfidTagError,
      isLoading: isCreateAdminRfidTagLoading
    }
  ] = useCreateAdminRfidTagMutation();
  const [updateAdminRfidTag] = useUpdateAdminRfidTagMutation();
  const [updateUserRfidTag] = useUpdateUserRfidTagMutation();
  const [createUserOrganizationsRoles] = useCreateUserOrganizationsRolesMutation();
  const [updateUserOrganizationsRoles] = useUpdateUserOrganizationsRolesMutation();
  const [deleteUserOrganizationsRoles] = useDeleteUserOrganizationsRoleMutation();
  const [createSuperAdminRole] = useCreateSuperAdminRoleMutation();

  useEffect(() => {
    if (isUpdateSuccess || isCreateSuccess) {
      toggleModal();
    }
    if (isUpdateError || isCreateError) {
      const errMsg = updateError?.data?.message || createError?.data?.message;

      if (errMsg?.includes('Email')) {
        setEmailError(errMsg);
      } else if (errMsg?.includes('RF ID')) {
        setIdTagError(errMsg);
      } else {
        setGeneralError(errMsg);
      }
    }
  }, [isUpdateError, isUpdateSuccess, isCreateError, isCreateSuccess, updateError, createError]);

  const [superAdminRoleId] = useState(roles.find((role) => role.name == ROLES.SUPER_ADMIN)?.id);
  const loggedInUserType = useSelector(selectUserType);
  const isSuperAdmin = loggedInUserType.isSuperAdmin;
  const isAdmin = loggedInUserType.isAdmin;
  const [selectedProviders, setSelectedProviders] = useState(() => {
    return (
      userData.user_roles
        ?.filter((userRole) => userRole.role.isAdmin)
        .flatMap((userRole) => userRole.provider.map((prov) => prov._id)) || []
    );
  });
  const superAdminOrgRole = {
    user_id: userData.id,
    organization_id: null,
    role_id: superAdminRoleId,
    id: isSuperAdmin?.id
  };
  const initialUserData = {
    email: userData.email || '',
    username: userData.userName || '',
    id_tag: userData.rfidTags ? userData.rfidTags[0].value : null,
    parentOrganization: userData.parentOrganization?.id
  };

  const [initialOrgRoles] = useState(
    () =>
      userData?.userOrganizationRoles?.reduce((accumulator, organizationRoles) => {
        accumulator.push({
          user_id: userData.id,
          organization_id: organizationRoles?.organization?.id,
          role_id: organizationRoles.role.id,
          userOrgRole_id: organizationRoles.id
        });
        return accumulator;
      }, []) || []
  );

  const [isEditModal] = useState(Object.keys(userData).length > 0);
  const [canSubmit, setCanSubmit] = useState(false);
  const [username, setUsername] = useState(initialUserData.username);
  const [usernameError, setUsernameError] = useState('');
  const [email, setEmail] = useState(initialUserData.email);
  const [emailError, setEmailError] = useState('');
  const [idTag, setIdTag] = useState(initialUserData.id_tag);
  const [parentOrganizationId, setParentOrganizationId] = useState(
    initialUserData.parentOrganization
  );
  const [idTagError, setIdTagError] = useState('');
  const [generalError, setGeneralError] = useState(null);
  const [superAdminCheck, setSuperAdminCheck] = useState(
    !!userData?.userOrganizationRoles?.find((role) => role.role.name === ROLES.SUPER_ADMIN)
  );
  const [providerAdminCheck, setProviderAdminCheck] = useState(
    !!userData?.userOrganizationRoles?.find((role) => role.role.name === ROLES.ADMIN)
  );
  const [orgRolesToDelete, setOrgRolesToDelete] = useState([]);
  const [scratchOrgRoles, setScratchOrgRoles] = useState([...initialOrgRoles]);

  const formattedOrganizations = useMemo(() => {
    return organizations.map((organization) => {
      const orgType = organizationTypes?.find(
        (item) => item.id === organization.organizationTypeId
      );
      return {
        ...organization,
        name: `${organization.name} ${
          orgType?.name === ORGANIZATION_TYPE.PROVIDER ? `(${orgType.name})` : '' ?? ''
        }`
      };
    });
  }, [organizations, organizationTypes]);

  const [rolesSansSuperAdmin] = useState(roles.filter((role) => role.name !== ROLES.SUPER_ADMIN));
  const disabledSelectValues = scratchOrgRoles.reduce((acc, orgRole) => {
    if (orgRole.organization_id !== undefined) {
      acc.push(orgRole.organization_id);
    }
    return acc;
  }, []);
  const { t } = useTranslation();

  const setErrorMsg = {
    username: setUsernameError,
    email: setEmailError,
    id_tag: setIdTagError,
    super_admin: () => null,
    provider_admin: () => null,
    roles: () => null,
    parentOrganizationId: () => null
  };

  const validate = {
    username: (value) => value.length === 0 || (value.length >= 3 && value.length <= 20),
    email: validator.isEmail,
    id_tag: (value) => (value ? value.length <= 20 : true),
    super_admin: () => true, //no validation
    provider_admin: () => true, //no validation
    roles: () => true, //no validation
    parentOrganizationId: () => true //no validation
  };

  const validationRef = useRef({
    email: validate.email(email),
    username: validate.username(username),
    id_tag: validate.id_tag(idTag),
    super_admin: validate.super_admin(superAdminCheck),
    provider_admin: validate.provider_admin(providerAdminCheck),
    roles: validate.roles(scratchOrgRoles),
    parentOrganizationId: true //no validation
  }); //keep track o all validation fields here.

  const setInputValue = {
    username: setUsername,
    email: setEmail,
    id_tag: setIdTag,
    super_admin: setSuperAdminCheck,
    provider_admin: setProviderAdminCheck,
    roles: () => null, //handled onChange
    parentOrganizationId: setParentOrganizationId
  };

  const handleRemoveOrg = (e, orgRoleIndex, updatedScratchOrgRoles, updatedOrgRolesToDelete) => {
    const id = updatedScratchOrgRoles[orgRoleIndex];

    if (id) {
      updatedOrgRolesToDelete.push(id);
    }

    const indexToDelete = updatedScratchOrgRoles.findIndex(
      (scratchOrgRole) => scratchOrgRole.userOrgRole_id === id
    );

    updatedScratchOrgRoles.splice(indexToDelete, 1);
    setScratchOrgRoles(updatedScratchOrgRoles);
    setOrgRolesToDelete(updatedOrgRolesToDelete);
  };

  const handleDropdown = (e, orgRoleIndex, updatedScratchOrgRoles, updatedOrgRolesToDelete) => {
    updatedScratchOrgRoles[orgRoleIndex].user_id = userData.id;
    updatedScratchOrgRoles[orgRoleIndex].organization_id = +e.target.value;
    updatedScratchOrgRoles[orgRoleIndex].role_id = rolesSansSuperAdmin[0].id;

    const currentOrgRoleId = updatedScratchOrgRoles[orgRoleIndex].id;

    if (currentOrgRoleId) {
      const indexToDelete = updatedOrgRolesToDelete.indexOf(currentOrgRoleId);
      if (indexToDelete > -1) {
        updatedOrgRolesToDelete.splice(indexToDelete, 1);
      } else {
        updatedOrgRolesToDelete.push(currentOrgRoleId);
      }
    }
    setScratchOrgRoles(updatedScratchOrgRoles);
    setOrgRolesToDelete(updatedOrgRolesToDelete);
  };

  const handleRoleRadio = (e, orgRoleIndex, updatedScratchOrgRoles) => {
    updatedScratchOrgRoles[orgRoleIndex].role_id = +e.target.value;
    setScratchOrgRoles(updatedScratchOrgRoles);
  };

  const handleValidation = (id, value) => {
    const isValid = validate[id](value);
    validationRef.current[id] = isValid;
    !isValid ? setErrorMsg[id](usersErrorMsg[id]) : setErrorMsg[id]('');
    handleCanSubmit();
  };

  const onChange = (e, index) => {
    const { type, id, name, value, checked } = e.target;
    let valueToSet = checked || value;
    // TODO (Alex): Refactor: onChange should not enforce the ID I need to use! Found in multiple places.
    //  Search for setInputValue[id]
    if (id === 'roles') {
      const updatedScratchOrgRoles = scratchOrgRoles.map((orgRole) => orgRole);
      const updatedOrgRolesToDelete = orgRolesToDelete.map((orgRole) => orgRole);
      switch (type) {
        case 'radio':
          handleRoleRadio(e, index, updatedScratchOrgRoles);
          break;
        case 'select-one':
          handleDropdown(e, index, updatedScratchOrgRoles, updatedOrgRolesToDelete);
          break;
        case 'button':
          handleRemoveOrg(e, index, updatedScratchOrgRoles, updatedOrgRolesToDelete);
          break;

        default:
          break;
      }
      valueToSet = updatedScratchOrgRoles;
    }
    if (id === 'super_admin') {
      handleSuperAdminToggle();
    }

    // TODO (Alex): Refactor: onChange should not enforce the ID I need to use! THIS IS WHY
    if (type === 'checkbox' && id === 'roles') {
      valueToSet = checked ? value : '';
      setInputValue[name](valueToSet);
      handleValidation(name, valueToSet);
      return;
    }

    // TODO (Alex): Refactor: onChange should not enforce the ID I need to use! THIS IS WHY
    try {
      setInputValue[id](valueToSet);
      handleValidation(id, valueToSet);
    } catch (fmm) {
      setInputValue[name](valueToSet);
      handleValidation(name, valueToSet);
    }
  };

  const onBlur = (e) => {
    const { id, value } = e.target;
    setInputValue[id](value.trim());
  };

  const addEmptyScratch = () => {
    !scratchOrgRoles?.some((obj) => Object.keys(obj).length === 0) &&
      setScratchOrgRoles([...scratchOrgRoles, {}]);
  };

  const handleSuperAdminToggle = () => {
    if (!superAdminCheck) {
      //When checking SuperAdmin
      setProviderAdminCheck(false);
      setScratchOrgRoles([superAdminOrgRole]);
      setOrgRolesToDelete(initialOrgRoles);
    } else {
      //When unchecking SuperAdmin
      setScratchOrgRoles(initialOrgRoles);
      setOrgRolesToDelete(initialOrgRoles);
    }
  };

  const handleCanSubmit = () => {
    const allFieldsValid = Object.values(validationRef.current).every((value) => value === true);
    const providersValid = providerAdminCheck
      ? selectedProviders.every((provider) => provider !== '')
      : true;

    setCanSubmit(allFieldsValid && providersValid);
  };

  useEffect(() => {
    handleCanSubmit();
  }, []);
  const handleSubmit = async (e) => {
    e.preventDefault();
    const userInfo = {
      email,
      userName: username
    };
    if (parentOrganizationId) {
      userInfo.parentOrganizationId = +parentOrganizationId;
    }
    if (isEditModal) {
      try {
        if (isAdmin) {
          await updateOrganizationAdmin({
            orgId: parentOrganizationId ? +parentOrganizationId : null,
            userInfo,
            where: {
              id: +userData.id
            },
            cacheParams
          });
          !!userData.rfidTags
            ? await updateAdminRfidTag({
                userId: userData?.id,
                data: { value: idTag },
                where: { value: userData?.rfidTags[0].value }
              })
            : !!idTag &&
              (await createAdminRfidTag({
                userId: userData?.id,
                value: idTag,
                status: RFID_TAG_STATUS.ACTIVE
              }));
          // TODO (IONUT): Handle uniq constraint RFID error -> createAdminRfidTagError
        } else {
          await updateUserData({
            id: userData.id,
            userInfo: { id: userData.id, ...userInfo },
            cacheParams
          });

          superAdminCheck &&
            (await createSuperAdminRole({
              userId: userData?.id,
              roleId: superAdminRoleId
            }));
          !!userData.rfidTags
            ? await updateUserRfidTag({
                rfidTagId: userData?.rfidTags[0]?.id,
                data: { userId: userData?.id, value: idTag }
              })
            : !!idTag &&
              (await createUserRfidTag({
                userId: userData?.id,
                value: idTag,
                status: RFID_TAG_STATUS.ACTIVE
              }));
        }

        const updateOrgRoles = scratchOrgRoles.map((item) => {
          const { userOrgRole_id, user_id, role_id, organization_id } = item;
          if (!organization_id) {
            return null;
          }
          if (!!userOrgRole_id) {
            return updateUserOrganizationsRoles({
              id: userOrgRole_id,
              user_id,
              role_id,
              organization_id
            });
          }
          return createUserOrganizationsRoles({ user_id, role_id, organization_id });
        });

        const deleteOrgRoles = orgRolesToDelete.map((item) => {
          const id = item.userOrgRole_id;
          return deleteUserOrganizationsRoles({ id });
        });
        await Promise.all([...updateOrgRoles, ...deleteOrgRoles]);

        refetch();
        toggleModal();
      } catch (error) {
        console.error('Error updating user:', error);
      }
    } else {
      try {
        const user = await createUser(userInfo);
        const userId = user.data?.id;
        isAdmin && idTag
          ? await createAdminRfidTag({ userId, value: idTag, status: RFID_TAG_STATUS.ACTIVE })
          : await createUserRfidTag({ userId, value: idTag, status: RFID_TAG_STATUS.ACTIVE });

        !!scratchOrgRoles.length &&
          (await Promise.all(
            scratchOrgRoles.map((item) =>
              createUserOrganizationsRoles({
                user_id: userId,
                role_id: item.role_id,
                organization_id: item.organization_id
              })
            )
          ));
        refetch();
        toggleModal();
      } catch (error) {
        console.error('Error creating user:', error);
      }
    }
  };
  const renderTextInput = () => (
    <>
      <FormInput
        id='username'
        label={t('alias')}
        value={username}
        placeholder={t('type_alias_here')}
        errorMessage={t(usernameError)}
        onChange={onChange}
        onBlur={onBlur}
      />
      <FormInput
        id='email'
        label={`${t('email')}*`}
        type={inputTypes.email}
        value={email}
        placeholder={t('type_the_contact_email')}
        errorMessage={t(emailError)}
        onChange={onChange}
        onBlur={onBlur}
      />
      <FormInput
        id='id_tag'
        label={t('rf_id')}
        value={idTag}
        placeholder={t('type_rf_id')}
        errorMessage={t(idTagError)}
        onChange={onChange}
        onBlur={onBlur}
      />
    </>
  );
  const renderRoles = (isEnabled) => {
    return (
      <div
        className={cx('role-settings__wrap', {
          'form__disable-input': !isEnabled
        })}>
        <RoleSettings
          id='roles'
          onChange={isEnabled ? onChange : () => null}
          handleChangeParent={setParentOrganizationId}
          organizations={formattedOrganizations}
          scratchOrgRoles={scratchOrgRoles}
          setScratchOrgRoles={setScratchOrgRoles}
          rolesSansSuperAdmin={rolesSansSuperAdmin}
          disabledSelectValues={disabledSelectValues}
          orgRolesToDelete={orgRolesToDelete}
          setOrgRolesToDelete={setOrgRolesToDelete}
          parentOrganizationId={parentOrganizationId}
        />
        <IfRender condition={disabledSelectValues.length !== organizations.length}>
          <Button className='button__charlie' type='button' onClick={addEmptyScratch}>
            {t('plus_add_organization')}
          </Button>
        </IfRender>
      </div>
    );
  };

  return (
    <ModalFrame onClose={toggleModal} closeOnBgClick={!isUpdateLoading}>
      <IfRender condition={!isUpdateLoading} otherwise={<LoadingAnimation dark />}>
        <form id='edit-user' className='modal-form'>
          <div className='modal-form__header'>
            <h2>{isEditModal ? t('edit_user') : t('add_user')}</h2>
            <Button
              className='button__icon color--alfa-foreground'
              iconName='close'
              onClick={toggleModal}
            />
          </div>
          <fieldset className='modal-form__fieldset'>{renderTextInput()}</fieldset>
          <fieldset className='modal-form__fieldset'>
            <IfRender condition={isSuperAdmin}>
              <FormInput
                id='super_admin'
                wrapClass='super-admin__wrap'
                labelClass='super-admin__label'
                label='Super Admin'
                type={inputTypes.checkbox}
                checked={superAdminCheck}
                onChange={(e) => {
                  onChange(e);
                }}
              />
            </IfRender>
            {/*There's no provider concept anymore. Don't think we need to keep this.*/}
            {/*<IfRender*/}
            {/*    condition={(isSuperAdmin)}>*/}
            {/*  <FormInput*/}
            {/*      id='provider_admin'*/}
            {/*      wrapClass='super-admin__wrap'*/}
            {/*      labelClass='super-admin__label'*/}
            {/*      label='Provider Admin'*/}
            {/*      type={inputTypes.checkbox}*/}
            {/*      checked={providerAdminCheck}*/}
            {/*      onChange={(e) => {*/}
            {/*        onChange(e);*/}
            {/*      }}*/}
            {/*  />*/}
            {/*</IfRender>*/}
            {renderRoles(userAuthData.user.id !== userData._id)}
          </fieldset>

          <div className='modal-form__footer'>
            <Button
              className='button__alfa'
              label={t('save')}
              type='submit'
              disabled={!canSubmit}
              onClick={(e) => {
                handleSubmit(e);
              }}
            />
          </div>
          {generalError && <p className='input__label input__label--error'>{generalError}</p>}
        </form>
      </IfRender>
    </ModalFrame>
  );
}

UserModal.propTypes = {
  cacheParams: PropTypes.object,
  userData: PropTypes.object,
  roles: PropTypes.array,
  toggleModal: PropTypes.func,
  userAuthData: PropTypes.object,
  organizations: PropTypes.array,
  providers: PropTypes.shape({
    list: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string
      })
    )
  })
};
